import React from 'react';

/**
 * Ornament — reusable decorative SVG marker.
 *
 * Inspired by traditional kolam / pīṭham flourishes; intentionally minimal
 * so it reads as a refined accent rather than ethnic kitsch.
 *
 *   <Ornament />                       gold, 32px, default
 *   <Ornament size={48} variant="lotus" tone="navy" />
 */
const Ornament = ({
    size = 32,
    tone = 'gold',          // 'gold' | 'saffron' | 'navy' | 'cream'
    variant = 'flourish',   // 'flourish' | 'lotus' | 'diamond' | 'rule'
    className = '',
    style = {},
}) => {
    const colour = ({
        gold:    'var(--sw-gold)',
        saffron: 'var(--sw-saffron)',
        navy:    'var(--sw-navy)',
        cream:   'var(--sw-cream-deep)',
    })[tone] || 'var(--sw-gold)';

    const commonProps = {
        width: size,
        height: variant === 'rule' ? Math.max(8, Math.round(size / 4)) : size,
        viewBox: variant === 'rule' ? '0 0 200 16' : '0 0 64 64',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        'aria-hidden': 'true',
        className: `sw-ornament ${className}`.trim(),
        style,
    };

    if (variant === 'flourish') {
        // Symmetric ornament — two arcs meeting at a centre dot.
        return (
            <svg {...commonProps}>
                <g stroke={colour} strokeWidth="1.5" strokeLinecap="round" fill="none">
                    <path d="M4 32 C 14 32, 18 22, 28 22 C 34 22, 32 32, 32 32" />
                    <path d="M60 32 C 50 32, 46 22, 36 22 C 30 22, 32 32, 32 32" />
                    <path d="M4 32 C 14 32, 18 42, 28 42 C 34 42, 32 32, 32 32" />
                    <path d="M60 32 C 50 32, 46 42, 36 42 C 30 42, 32 32, 32 32" />
                </g>
                <circle cx="32" cy="32" r="2.5" fill={colour} />
                <circle cx="6"  cy="32" r="1.2" fill={colour} />
                <circle cx="58" cy="32" r="1.2" fill={colour} />
            </svg>
        );
    }

    if (variant === 'lotus') {
        // Eight-petalled lotus glyph (simplified).
        return (
            <svg {...commonProps}>
                <g stroke={colour} strokeWidth="1.5" fill="none" strokeLinejoin="round">
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                        <ellipse
                            key={deg}
                            cx="32" cy="14" rx="6" ry="14"
                            transform={`rotate(${deg} 32 32)`}
                        />
                    ))}
                </g>
                <circle cx="32" cy="32" r="3" fill={colour} />
            </svg>
        );
    }

    if (variant === 'diamond') {
        return (
            <svg {...commonProps}>
                <g stroke={colour} strokeWidth="1.5" fill="none">
                    <path d="M32 8 L56 32 L32 56 L8 32 Z" />
                    <path d="M32 18 L46 32 L32 46 L18 32 Z" />
                </g>
                <circle cx="32" cy="32" r="2" fill={colour} />
            </svg>
        );
    }

    if (variant === 'rule') {
        // A horizontal "title rule" — three diamonds with hairlines on either side.
        return (
            <svg {...commonProps}>
                <g stroke={colour} strokeWidth="1" fill="none">
                    <line x1="0"   y1="8" x2="78"  y2="8" />
                    <line x1="122" y1="8" x2="200" y2="8" />
                    <path d="M88 8 L94 2 L100 8 L94 14 Z" fill={colour} />
                    <path d="M100 8 L106 4 L112 8 L106 12 Z" fill={colour} />
                    <path d="M112 8 L118 2 L124 8 L118 14 Z" fill={colour} />
                </g>
            </svg>
        );
    }

    return null;
};

export default Ornament;
